import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { buildImageUrl } from '../../../utils/imageUrl';

/* ──────────────────────────────────────────────────────────────────────────
 * Shared design language for the public (luxury furniture) website.
 * Palette + type are fixed by brand: Cream #f4ece0 / Espresso #241811 /
 * Terracotta #b5622f, Fraunces (display) + Inter (body).
 * Everything here is presentational only — no business logic, no API calls.
 * ────────────────────────────────────────────────────────────────────────── */

export const THEME = {
  cream: '#f4ece0',
  creamDeep: '#ece0cd',
  espresso: '#241811',
  ink: '#160f0a',
  terracotta: '#b5622f',
  clay: '#d98a52',
  text: '#221a14',
  muted: '#4a3d32',
};

const FALLBACK_IMG =
  'https://images.unsplash.com/photo-1683115099860-5379ac101bce?auto=format&fit=crop&w=1400&q=80';

/** Resolve any stored image path (full URL, Cloudinary id, /uploads/…) to a URL. */
export function img(url, fallback = FALLBACK_IMG) {
  if (!url) return fallback;
  if (/^https?:\/\//i.test(url)) return url;
  return buildImageUrl(url) || fallback;
}

export function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

/** Turn a raw category value into a display label ("living_room" → "Living Room"). */
export function labelize(value = '') {
  return String(value)
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function slugify(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/* ── Reveal-on-scroll: subtle fade+rise, respects reduced motion ───────────── */
export function useReveal() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, shown];
}

export function Reveal({ as: Tag = 'div', delay = 0, className = '', children, ...rest }) {
  const [ref, shown] = useReveal();
  return (
    <Tag
      ref={ref}
      className={cx('reveal', shown && 'reveal-in', className)}
      style={{ transitionDelay: shown ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* ── Small typographic building blocks ─────────────────────────────────────── */
export function Eyebrow({ children, index, className = '', tone = 'terracotta' }) {
  const color = tone === 'light' ? 'text-[#d98a52]' : 'text-[#b5622f]';
  return (
    <span
      className={cx(
        'inline-flex items-center gap-3 uppercase tracking-[0.24em] text-[11px] font-semibold',
        color,
        className
      )}
    >
      {index != null && (
        <span className="tabular-nums opacity-70">{String(index).padStart(2, '0')}</span>
      )}
      <span className="w-8 h-px bg-current opacity-60" />
      {children}
    </span>
  );
}

export function Display({ as: Tag = 'h2', className = '', children }) {
  return <Tag className={cx('font-display font-semibold leading-tight', className)}>{children}</Tag>;
}

/* ── Buttons: primary = filled espresso, ghost = outline, link = underline ──── */
function btnBase(size) {
  const pad = size === 'sm' ? 'px-5 py-2.5 text-[12px]' : 'px-7 py-3.5 text-[13px]';
  return cx(
    'inline-flex items-center justify-center gap-2 uppercase tracking-[0.14em]',
    'transition-all duration-300 select-none focus-visible:outline-none',
    'focus-visible:ring-2 focus-visible:ring-[#b5622f] focus-visible:ring-offset-2',
    pad
  );
}

export function PrimaryButton({ as, to, href, onClick, children, size, className = '', ...rest }) {
  const cls = cx(btnBase(size), 'bg-[#241811] text-[#f4ece0] hover:bg-[#3d2b1f]', className);
  if (to) return <Link to={to} className={cls} {...rest}>{children}</Link>;
  if (href) return <a href={href} className={cls} {...rest}>{children}</a>;
  return <button type="button" onClick={onClick} className={cls} {...rest}>{children}</button>;
}

export function GhostButton({ to, href, onClick, children, size, dark, className = '', ...rest }) {
  const border = dark
    ? 'border border-white/45 text-white hover:border-white hover:bg-white/10'
    : 'border border-[#241811]/25 text-[#241811] hover:border-[#241811] hover:bg-[#241811]/[0.04]';
  const cls = cx(btnBase(size), border, className);
  if (to) return <Link to={to} className={cls} {...rest}>{children}</Link>;
  if (href) return <a href={href} className={cls} {...rest}>{children}</a>;
  return <button type="button" onClick={onClick} className={cls} {...rest}>{children}</button>;
}

/* ── Skeleton block for progressive loading ────────────────────────────────── */
export function Skeleton({ className = '' }) {
  return <div className={cx('animate-pulse bg-black/[0.06]', className)} />;
}
